/**
 * =============================================================================
 * Página: CrmPlaybooks
 * Versão: 1.0.0
 *
 * Descrição:
 *   Listagem de Playbooks de Atividades por Workspace.
 *   - Header compacto + 4 KPI cards (Total / Ativos / Cadências ativas / Vínculos)
 *   - Seletor de workspace + busca
 *   - Cards de playbook com ações (editar / duplicar futura / excluir)
 *
 *   O editor (drag-and-drop de steps) é a próxima página `/crm/playbooks/:id`
 *   (item 1.13 da Fase 1) e ainda não está implementado nesta versão.
 *
 *   Toda leitura/escrita passa por RPCs `SECURITY DEFINER`:
 *   `list_activity_playbooks`, `upsert_activity_playbook`, `delete_activity_playbook`.
 *
 * Release notes:
 *   v1.0.0 - Criação (Fase 1 — itens 1.11 e 1.12 do Playbook de Vendas).
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListChecks,
  Plus,
  Search,
  Star,
  CheckCircle2,
  PlayCircle,
  Link2,
  Edit,
  Trash2,
  RefreshCw,
  Building2,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  ListPageContent,
} from '@/components/layout/list-page';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';

import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import {
  useActivityPlaybooks,
  type ActivityPlaybookListItem,
} from '@/hooks/useActivityPlaybooks';
import { createLogger } from '@/lib/logger';

const logger = createLogger('CrmPlaybooks');

// Chave de persistência do filtro de workspace selecionado
const WORKSPACE_FILTER_STORAGE_KEY = 'crm-playbooks-workspace-filter';

// ============================================================================
// Componente principal
// ============================================================================
export default function CrmPlaybooks() {
  const navigate = useNavigate();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  // Workspaces disponíveis (filtra na UI por enquanto; o opt-in
  // `playbook_mode_enabled` será aplicado via filtro futuro).
  const { workspaces = [], isLoading: isLoadingWorkspaces } =
    useCrmWorkspacesHierarchy();

  // Filtro de busca local
  const [search, setSearch] = useState('');

  // Filtro de workspace — aceita 'all' (todos workspaces) ou um UUID específico.
  // Padrão: 'all' (1 única chamada à RPC retorna playbooks de todos os workspaces).
  // Persistido em localStorage para preservar a última seleção.
  const [workspaceFilter, setWorkspaceFilter] = useState<string>(() => {
    if (typeof window === 'undefined') return 'all';
    return window.localStorage.getItem(WORKSPACE_FILTER_STORAGE_KEY) ?? 'all';
  });

  // Sincroniza filtro com a lista de workspaces:
  // - Se o UUID salvo não existe mais, volta para 'all'.
  useEffect(() => {
    if (workspaces.length === 0) return;
    if (workspaceFilter === 'all') return;
    const exists = workspaces.some((w) => w.id === workspaceFilter);
    if (!exists) {
      setWorkspaceFilter('all');
    }
  }, [workspaces, workspaceFilter]);

  // Persiste seleção
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(WORKSPACE_FILTER_STORAGE_KEY, workspaceFilter);
  }, [workspaceFilter]);

  // Hook único — 1 chamada à RPC `list_activity_playbooks`.
  // Quando filtro = 'all', envia null para a RPC retornar de todos os workspaces.
  const singleWs = useActivityPlaybooks(
    workspaceFilter === 'all' ? null : workspaceFilter,
  );

  const playbooks: ActivityPlaybookListItem[] = singleWs.playbooks;
  const isLoading = singleWs.isLoading;
  const isFetching = singleWs.isFetching;
  const refetch = () => {
    singleWs.refetch();
  };

  // Estado para diálogo de exclusão
  const [toDelete, setToDelete] = useState<ActivityPlaybookListItem | null>(null);

  // Filtragem por busca (client-side, dataset esperado é pequeno)
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return playbooks;
    return playbooks.filter((p) =>
      [p.name, p.description ?? ''].some((v) =>
        v.toLowerCase().includes(term),
      ),
    );
  }, [playbooks, search]);

  // KPIs
  const kpis = useMemo(() => {
    const total = playbooks.length;
    const ativos = playbooks.filter((p) => p.is_active).length;
    const cadencias = playbooks.reduce(
      (acc, p) => acc + (p.active_cadences_count ?? 0),
      0,
    );
    const vinculos = playbooks.reduce(
      (acc, p) => acc + (p.bindings_count ?? 0),
      0,
    );
    return { total, ativos, cadencias, vinculos };
  }, [playbooks]);

  // Handler de exclusão
  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await singleWs.deletePlaybook(toDelete.id);
      toast.success(`Playbook de Vendas "${toDelete.name}" excluído.`);
      setToDelete(null);
    } catch (err: any) {
      logger.error('Erro ao excluir playbook', err);
      toast.error(err?.message ?? 'Não foi possível excluir o playbook.');
    }
  };

  // Handler de criação — quando filtro é 'all', navega sem workspace_id
  // (o editor solicitará a seleção); caso contrário pré-vincula ao workspace.
  const handleCreate = () => {
    if (workspaceFilter && workspaceFilter !== 'all') {
      navigate(`/tenant/crm/playbooks/new?workspace_id=${workspaceFilter}`);
    } else {
      navigate(`/tenant/crm/playbooks/new`);
    }
  };

  return (
    <>
      <ListPageLayout>
        <ListPageHeader
          title="Playbook de Vendas"
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="xs"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`}
                />
              </Button>
              <Button size="xs" onClick={handleCreate}>
                <Plus className="mr-1.5" />
                Novo Playbook de Vendas
              </Button>
            </div>
          }
        />

        <ListPageKpis>
          <KpiCard
            color="purple"
            icon={ListChecks}
            label="Total"
            value={kpis.total}
          />
          <KpiCard
            color="green"
            icon={CheckCircle2}
            label="Ativos"
            value={kpis.ativos}
          />
          <KpiCard
            color="blue"
            icon={PlayCircle}
            label="Cadências em andamento"
            value={kpis.cadencias}
          />
          <KpiCard
            color="orange"
            icon={Link2}
            label="Vínculos com pipelines"
            value={kpis.vinculos}
          />
        </ListPageKpis>

        <ListPageFiltersBar isFetching={isFetching}>
          {/* Workspace — opção "Todos" como padrão (1 chamada agregada na RPC) */}
          <Select
            value={workspaceFilter}
            onValueChange={setWorkspaceFilter}
            disabled={isLoadingWorkspaces || workspaces.length === 0}
          >
            <SelectTrigger className="w-[100px] h-6 text-[11px] sm:w-[160px] sm:h-8 sm:text-sm">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <Building2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate min-w-0 flex-1 text-left">
                  {workspaceFilter === 'all'
                    ? 'Todos workspaces'
                    : workspaces.find((w) => w.id === workspaceFilter)?.name ||
                      'Workspace'}
                </span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="flex items-center gap-1.5">
                  <Building2 className="h-3 w-3 shrink-0 text-muted-foreground" />
                  Todos workspaces
                </span>
              </SelectItem>
              {workspaces.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  <span className="flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: w.color || '#6366f1' }}
                    />
                    {w.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Busca — mesmo padrão de CrmDeals */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar playbook..."
              className="pl-8 h-6 text-[11px] sm:h-8 sm:text-sm"
            />
          </div>
        </ListPageFiltersBar>

        <ListPageContent>
          {isLoading ? (
            <PlaybooksSkeleton />
          ) : workspaces.length === 0 ? (
            <EmptyState
              title="Nenhum workspace disponível"
              description="Crie um workspace de CRM para começar a usar playbooks."
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              title={
                search
                  ? 'Nenhum playbook encontrado'
                  : 'Nenhum playbook neste workspace'
              }
              description={
                search
                  ? 'Tente ajustar o termo de busca.'
                  : 'Clique em "Novo Playbook de Vendas" para criar a primeira cadência.'
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((p) => (
                <PlaybookCard
                  key={p.id}
                  playbook={p}
                  onEdit={() => navigate(`/tenant/crm/playbooks/${p.id}`)}
                  onDelete={() => setToDelete(p)}
                />
              ))}
            </div>
          )}
        </ListPageContent>
      </ListPageLayout>

      {/* Diálogo de exclusão */}
      <AlertDialog
        open={!!toDelete}
        onOpenChange={(open) => !open && setToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir playbook?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove permanentemente o playbook{' '}
              <strong>{toDelete?.name}</strong> e seus passos.
              {toDelete && toDelete.active_cadences_count > 0 && (
                <span className="block mt-2 text-destructive">
                  ⚠️ Existem {toDelete.active_cadences_count} cadência(s) ativa(s).
                  A exclusão será bloqueada — cancele-as antes.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={singleWs.isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={singleWs.isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {singleWs.isDeleting ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ============================================================================
// Card individual de playbook
// ============================================================================
function PlaybookCard({
  playbook,
  onEdit,
  onDelete,
}: {
  playbook: ActivityPlaybookListItem;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4 space-y-3">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="h-8 w-8 rounded-md flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
              style={{ backgroundColor: playbook.color ?? '#6b7280' }}
            >
              {playbook.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm truncate">{playbook.name}</h3>
              {playbook.description && (
                <p className="text-xs text-muted-foreground truncate">
                  {playbook.description}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {playbook.is_default && (
              <Badge variant="secondary" className="gap-1">
                <Star className="h-3 w-3" /> Padrão
              </Badge>
            )}
            {!playbook.is_active && <Badge variant="outline">Inativo</Badge>}
          </div>
        </div>

        {/* Métricas */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <Metric label="Passos" value={playbook.steps_count} />
          <Metric label="Vínculos" value={playbook.bindings_count} />
          <Metric label="Ativas" value={playbook.active_cadences_count} />
        </div>

        {/* Ações */}
        <div className="flex items-center justify-end gap-1 pt-1 border-t">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Edit className="h-3.5 w-3.5 mr-1" />
            Editar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-muted/50 rounded px-2 py-1 text-center">
      <div className="text-base font-semibold">{value}</div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
    </div>
  );
}

// ============================================================================
// Auxiliares — Skeleton + EmptyState
// ============================================================================
function PlaybooksSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-40 w-full" />
      ))}
    </div>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <ListChecks className="h-10 w-10 text-muted-foreground mb-3" />
      <h3 className="font-semibold mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-md">{description}</p>
    </div>
  );
}
